import type { Registry as Services } from '@ember/service';
import type { Dayjs } from 'dayjs';
import type { Carate } from 'lumen/models';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER,
  OVERRIDE_TYPES,
  type OverrideTypes,
  type OverrideVariant,
  PRIMA_VIEWS,
} from 'lumen/utils/constants/pricing-assistant-constants';

/**
 * Fall-back room order used when no hierarchy applies: category order first,
 * then average price priority within a category (same as the calendar day view).
 */
function compareByFallbackOrder(a: Carate, b: Carate): number {
  const categoryAIndex = OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER.indexOf(
    a.cemaCategory,
  );
  const categoryBIndex = OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER.indexOf(
    b.cemaCategory,
  );

  if (categoryAIndex !== categoryBIndex) {
    return categoryAIndex - categoryBIndex;
  }

  return a.averagePricePriority - b.averagePricePriority;
}

/**
 * Order room carates for the multi-rate table. When the BE provides a flattened
 * room-offset hierarchy (a list of `pmsRoomtype`s in display order), rooms in it
 * follow that order. Rooms absent from an incomplete hierarchy are appended after
 * the listed ones, ordered by their category and then average price priority.
 * With no hierarchy at all, every room falls back to that same order.
 */
export function sortRoomCaratesByOffsetHierarchy(
  carates: Carate[],
  flattenedRoomOffsetHierarchy: string[],
): Carate[] {
  if (!flattenedRoomOffsetHierarchy.length) {
    return [...carates].sort(compareByFallbackOrder);
  }

  return [...carates].sort((a, b) => {
    const indexA = flattenedRoomOffsetHierarchy.indexOf(a.pmsRoomtype);
    const indexB = flattenedRoomOffsetHierarchy.indexOf(b.pmsRoomtype);

    // Both rooms are in the hierarchy: follow its order.
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // Exactly one room is in the hierarchy: it comes before the unlisted one.
    if (indexA !== -1) {
      return -1;
    }
    if (indexB !== -1) {
      return 1;
    }

    // Neither room is in the hierarchy: use the standard fall-back order.
    return compareByFallbackOrder(a, b);
  });
}

export function getOverrideVariant(
  overrideType: OverrideTypes | null | undefined,
): OverrideVariant | undefined {
  if (!overrideType) {
    return undefined;
  }
  return overrideType === OVERRIDE_TYPES.PRICE_RANGE
    ? 'bullet'
    : 'automation-off';
}

/* Calculate the total rooms for sale by adding the physical rooms available and the current overbooking. */
export function calculateTotalRoomsForSale(
  physicalRoomsAvailable: number,
  currentOverbooking: number,
): number {
  return physicalRoomsAvailable + currentOverbooking;
}

/**
 * The from/to stay date range only applies in table view, for DRP hotels with
 * the PRICING_MANAGER_RMS_PIVOT_TABLE toggle on. Shared by the route and the
 * controller so both gate the range identically.
 */
export function isStayDateRangeEnabled(
  featureToggle: Services['feature-toggle'],
  access: Services['access'],
  view: string | null,
): boolean {
  return (
    featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.PRICING_MANAGER_RMS_PIVOT_TABLE,
    ) &&
    view === PRIMA_VIEWS.TABLE &&
    access.hasPricingAssistantDynamicRatePricingFeature
  );
}

/**
 * The week table shows the current week while the stay date range includes today
 * (same as before the range existed), otherwise the week the range starts in.
 * Only the current month can start before today, so it keeps opening on today.
 */
export function getWeekForStayDateRange(
  rangeStart: Dayjs,
  today: Dayjs,
): string {
  return (rangeStart.isBefore(today, 'day') ? today : rangeStart)
    .startOf('week')
    .format('YYYY-MM-DD');
}
